<!--
 * @Description: 项目核场记录详情
-->
<template>
  <scroll-layout class="inspection-record-detail">
    <template slot="header">
      <div class="page-header">
        <el-button type="text" class="back-btn" @click="goBack">
          <i class="el-icon-arrow-left"></i>
          返回列表
        </el-button>
        <h2 class="page-title">
          核场记录详情
          <span v-if="detail.recordCode" class="record-code-tag">{{ detail.recordCode }}</span>
        </h2>
        <div class="header-actions">
          <el-button
            v-if="isSubmitted && !hideSubmitInitiation"
            type="primary"
            size="small"
            @click="handleSubmitInitiation">
            <i class="el-icon-position"></i>
            提交立项
          </el-button>
          <el-button v-if="detail.id && !isInitiated" type="primary" size="small" @click="goEdit">
            <i class="el-icon-edit"></i>
            去编辑
          </el-button>
          <el-button size="small" @click="goBack">关闭</el-button>
        </div>
      </div>
    </template>

    <div class="detail-body" v-loading="loading">
      <!-- 填写提示 -->
      <div class="ai-notice">
        <i class="el-icon-lightbulb"></i>
        <span>
          💡 填写提示：标注
          <span class="required">*</span>
          的编辑项均为必填；带有「AI」标识的编辑项支持智能检索填充，AI 生成内容仅供参考，
          请务必按照实际情况进行核验确认或修改
        </span>
      </div>

      <!-- 状态信息 -->
      <div class="status-bar card">
        <div class="status-info">
          <div class="status-left">
            <span class="status-label">记录状态：</span>
            <span class="status-tag" :class="getStatusClass(detail.status)">
              <i :class="getStatusIcon(detail.status)"></i>
              {{ getStatusText(detail.status) }}
            </span>
          </div>
          <div class="status-meta">
            <span class="meta-item" v-if="detail.creatorName">
              <i class="el-icon-user"></i>
              创建人：{{ detail.creatorName }}
            </span>
            <span class="meta-item" v-if="detail.createTime">
              <i class="el-icon-time"></i>
              创建时间：{{ detail.createTime }}
            </span>
            <span class="meta-item" v-if="detail.lastModifierName">
              <i class="el-icon-edit-outline"></i>
              最后编辑：{{ detail.lastModifierName }}
              <span v-if="detail.lastModifiedTime" class="meta-time">
                {{ detail.lastModifiedTime }}
              </span>
            </span>
            <span class="meta-item" v-if="detail.submitTime">
              <i class="el-icon-check"></i>
              提交时间：{{ detail.submitTime }}
            </span>
          </div>
        </div>
      </div>

      <!-- 项目基础信息 -->
      <div class="section-card">
        <div class="section-header">
          <div class="section-header-left">
            <div class="section-icon" style="background: #eff6ff; color: #3b82f6">
              <i class="el-icon-info"></i>
            </div>
            <div>
              <h3 class="section-title">项目基础信息</h3>
              <p class="section-subtitle">项目名称、位置、规模等</p>
            </div>
          </div>
        </div>
        <div class="section-content">
          <div class="info-grid">
            <div class="info-row">
              <span class="info-label">项目名称</span>
              <span class="info-value">{{ detail.projectName || '--' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">站点名称</span>
              <span class="info-value">{{ detail.stationName || '--' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">站点性质</span>
              <span class="info-value">{{ detail.siteNature || '--' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">核场人</span>
              <span class="info-value">{{ detail.creatorName || '--' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">核场时间</span>
              <span class="info-value">{{ detail.submitTime || '--' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">所属BD</span>
              <span class="info-value">{{ detail.bdAccountName || '--' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">所在省份</span>
              <span class="info-value">{{ detail.province || '--' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">所在城市</span>
              <span class="info-value">{{ detail.city || '--' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">区/县</span>
              <span class="info-value">{{ detail.district || '--' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">详细地址</span>
              <span class="info-value">{{ detail.detailAddress || '--' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">项目建设时间</span>
              <span class="info-value">{{ detail.constructionTime || '--' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">停车位数量</span>
              <span class="info-value">{{ detail.parkingSpots ?? '--' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">车位使用情况</span>
              <span class="info-value">{{ formatParkingUsage(detail.parkingUsage) }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">停车场信号</span>
              <span class="info-value">
                <div v-if="signalList.length" class="signal-tags">
                  <span v-for="s in signalList" :key="s" class="signal-tag">{{ s }}</span>
                </div>
                <span v-else>--</span>
              </span>
            </div>
            <div class="info-row">
              <span class="info-label">停车场结构</span>
              <span class="info-value">{{ detail.parkingStructure || '--' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">周边200米快充站</span>
              <span class="info-value">{{ formatFastCharge(detail.hasNearbyFastCharge) }}</span>
            </div>
            <div v-if="detail.parkingStructure === '地上+地下'" class="info-row">
              <span class="info-label">地上+地下道闸是否打通</span>
              <span class="info-value">{{ detail.barrierConnected || '--' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">私家车位比例</span>
              <span class="info-value">{{ detail.privateParkingRatio || '--' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">我司用电性质</span>
              <span class="info-value">{{ detail.electricityUsageType || '--' }}</span>
            </div>
          </div>

          <!-- 地上车辆情况 -->
          <div class="sub-group">
            <div class="sub-group-title">
              <i class="el-icon-top"></i>
              地上车辆情况
            </div>
            <div class="info-grid info-grid-3">
              <div class="info-row">
                <span class="info-label">地上无桩新能源车</span>
                <span class="info-value">{{ detail.aboveGroundNoPileEvCount ?? '--' }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">地上混动车</span>
                <span class="info-value">{{ detail.aboveGroundHybridEvCount ?? '--' }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">地上纯电车</span>
                <span class="info-value">{{ detail.aboveGroundPureEvCount ?? '--' }}</span>
              </div>
            </div>
          </div>

          <!-- 地下车辆情况 -->
          <div class="sub-group">
            <div class="sub-group-title">
              <i class="el-icon-bottom"></i>
              地下车辆情况
            </div>
            <div class="info-grid info-grid-3">
              <div class="info-row">
                <span class="info-label">地下无桩新能源车</span>
                <span class="info-value">{{ detail.undergroundNoPileEvCount ?? '--' }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">地下混动车</span>
                <span class="info-value">{{ detail.undergroundHybridEvCount ?? '--' }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">地下纯电车</span>
                <span class="info-value">{{ detail.undergroundPureEvCount ?? '--' }}</span>
              </div>
            </div>
          </div>

          <!-- 总计 -->
          <div class="sub-group">
            <div class="sub-group-title">
              <i class="el-icon-data-analysis"></i>
              总计
            </div>
            <div class="info-grid info-grid-3">
              <div class="info-row">
                <span class="info-label">无桩电车总数</span>
                <span class="info-value highlight-text">{{ detail.noPileEvCount ?? '--' }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">混动总数</span>
                <span class="info-value highlight-text">{{ detail.hybridEvCount ?? '--' }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">纯电总数</span>
                <span class="info-value highlight-text">{{ detail.pureEvCount ?? '--' }}</span>
              </div>
            </div>
          </div>

          <div v-if="detail.remark" class="info-row info-row-block">
            <span class="info-label">备注说明</span>
            <div class="info-value-block">{{ detail.remark }}</div>
          </div>
        </div>
      </div>

      <!-- 项目图片 -->
      <div class="section-card">
        <div class="section-header">
          <div class="section-header-left">
            <div class="section-icon" style="background: #fdf4e8; color: #c9915e">
              <i class="el-icon-picture-outline"></i>
            </div>
            <div>
              <h3 class="section-title">项目图片</h3>
              <p class="section-subtitle">现场采集照片</p>
            </div>
          </div>
        </div>
        <div class="section-content">
          <!-- 待分类 -->
          <div class="photo-block">
            <h4 class="photo-block-title">
              待分类
              <span class="photo-count">{{ (detail.unclassifiedPhotos || []).length }} 张</span>
            </h4>
            <div
              v-if="detail.unclassifiedPhotos && detail.unclassifiedPhotos.length"
              class="photo-grid">
              <el-image
                v-for="(img, idx) in displayPhotos('unclassifiedPhotos')"
                :key="'unclassified-' + idx"
                :src="img"
                :preview-src-list="detail.unclassifiedPhotos"
                :initial-index="idx"
                fit="cover"
                class="photo-img" />
              <div
                v-if="detail.unclassifiedPhotos.length >= MAX_DISPLAY_PHOTOS"
                class="photo-more"
                @click="toggleExpandPhotos('unclassifiedPhotos')">
                <i
                  :class="
                    isExpanded('unclassifiedPhotos') ? 'el-icon-arrow-up' : 'el-icon-more'
                  "></i>
                {{ isExpanded('unclassifiedPhotos') ? '收起' : '查看全部' }}
              </div>
            </div>
          </div>

          <!-- 项目现场图 -->
          <div class="photo-block">
            <h4 class="photo-block-title">
              项目现场图
              <span v-if="detail.scenePhotos && detail.scenePhotos.length" class="photo-count">
                {{ detail.scenePhotos.length }} 张
              </span>
            </h4>
            <div v-if="detail.scenePhotos && detail.scenePhotos.length" class="photo-grid">
              <el-image
                v-for="(img, idx) in displayPhotos('scenePhotos')"
                :key="'scene-' + idx"
                :src="img"
                :preview-src-list="detail.scenePhotos"
                :initial-index="idx"
                fit="cover"
                class="photo-img" />
              <div
                v-if="detail.scenePhotos.length >= MAX_DISPLAY_PHOTOS"
                class="photo-more"
                @click="toggleExpandPhotos('scenePhotos')">
                <i :class="isExpanded('scenePhotos') ? 'el-icon-arrow-up' : 'el-icon-more'"></i>
                {{ isExpanded('scenePhotos') ? '收起' : '查看全部' }}
              </div>
            </div>
            <div v-else class="empty-photo">暂无图片</div>
          </div>

          <!-- 无桩电车图 -->
          <div class="photo-block">
            <h4 class="photo-block-title">
              无桩电车图
              <span v-if="detail.evPhotos && detail.evPhotos.length" class="photo-count">
                {{ detail.evPhotos.length }} 张
              </span>
              <span class="photo-block-actions">
                <el-button
                  size="mini"
                  type="text"
                  icon="el-icon-view"
                  :disabled="!evExtractedList.length"
                  @click="handleViewEvExtractedData">
                  查看提取数据
                </el-button>
                <el-button
                  size="mini"
                  type="text"
                  icon="el-icon-download"
                  :disabled="!evExtractedList.length"
                  @click="handleExportEvExtractedData">
                  导出提取数据
                </el-button>
              </span>
            </h4>
            <div v-if="detail.evPhotos && detail.evPhotos.length" class="photo-grid">
              <div
                v-for="(img, idx) in displayPhotos('evPhotos')"
                :key="'ev-' + idx"
                class="photo-item">
                <el-image
                  :src="img"
                  :preview-src-list="detail.evPhotos"
                  :initial-index="idx"
                  fit="cover"
                  class="photo-img" />
                <div class="photo-location-tags">
                  <span
                    class="loc-tag ground-tag"
                    :class="{
                      active: getEvLocation(idx) === '地上',
                      inactive: getEvLocation(idx) !== '地上',
                    }">
                    地上
                  </span>
                  <span
                    class="loc-tag underground-tag"
                    :class="{
                      active: getEvLocation(idx) === '地下',
                      inactive: getEvLocation(idx) !== '地下',
                    }">
                    地下
                  </span>
                </div>
                <div
                  class="ev-info-tags"
                  v-if="
                    (detail.evPhotoBrands && detail.evPhotoBrands[idx]) ||
                    (detail.evPhotoModels && detail.evPhotoModels[idx]) ||
                    (detail.evPhotoLicensePlates && detail.evPhotoLicensePlates[idx])
                  ">
                  <span
                    class="ev-info-tag"
                    v-if="detail.evPhotoBrands && detail.evPhotoBrands[idx]">
                    {{ detail.evPhotoBrands[idx] }}
                  </span>
                  <span
                    class="ev-info-tag"
                    v-if="detail.evPhotoModels && detail.evPhotoModels[idx]">
                    {{ detail.evPhotoModels[idx] }}
                  </span>
                  <span
                    class="ev-info-tag"
                    v-if="detail.evPhotoLicensePlates && detail.evPhotoLicensePlates[idx]">
                    {{ detail.evPhotoLicensePlates[idx] }}
                  </span>
                </div>
              </div>
              <div
                v-if="detail.evPhotos.length >= MAX_DISPLAY_PHOTOS"
                class="photo-more"
                @click="toggleExpandPhotos('evPhotos')">
                <i :class="isExpanded('evPhotos') ? 'el-icon-arrow-up' : 'el-icon-more'"></i>
                {{ isExpanded('evPhotos') ? '收起' : '查看全部' }}
              </div>
            </div>
            <div v-else class="empty-photo">暂无图片</div>
          </div>

          <!-- 私人充电桩图 -->
          <div class="photo-block">
            <h4 class="photo-block-title">
              私人充电桩图
              <span
                v-if="detail.privatePilePhotos && detail.privatePilePhotos.length"
                class="photo-count">
                {{ detail.privatePilePhotos.length }} 张
              </span>
            </h4>
            <div
              v-if="detail.privatePilePhotos && detail.privatePilePhotos.length"
              class="photo-grid">
              <el-image
                v-for="(img, idx) in displayPhotos('privatePilePhotos')"
                :key="'private-' + idx"
                :src="img"
                :preview-src-list="detail.privatePilePhotos"
                :initial-index="idx"
                fit="cover"
                class="photo-img" />
              <div
                v-if="detail.privatePilePhotos.length >= MAX_DISPLAY_PHOTOS"
                class="photo-more"
                @click="toggleExpandPhotos('privatePilePhotos')">
                <i
                  :class="
                    isExpanded('privatePilePhotos') ? 'el-icon-arrow-up' : 'el-icon-more'
                  "></i>
                {{ isExpanded('privatePilePhotos') ? '收起' : '查看全部' }}
              </div>
            </div>
            <div v-else class="empty-photo">暂无图片</div>
          </div>

          <!-- 竞品充电桩图 -->
          <div class="photo-block">
            <h4 class="photo-block-title">
              竞品充电桩图
              <span
                v-if="detail.competePilePhotos && detail.competePilePhotos.length"
                class="photo-count">
                {{ detail.competePilePhotos.length }} 张
              </span>
            </h4>
            <div
              v-if="detail.competePilePhotos && detail.competePilePhotos.length"
              class="photo-grid">
              <el-image
                v-for="(img, idx) in displayPhotos('competePilePhotos')"
                :key="'compete-' + idx"
                :src="img"
                :preview-src-list="detail.competePilePhotos"
                :initial-index="idx"
                fit="cover"
                class="photo-img" />
              <div
                v-if="detail.competePilePhotos.length >= MAX_DISPLAY_PHOTOS"
                class="photo-more"
                @click="toggleExpandPhotos('competePilePhotos')">
                <i
                  :class="
                    isExpanded('competePilePhotos') ? 'el-icon-arrow-up' : 'el-icon-more'
                  "></i>
                {{ isExpanded('competePilePhotos') ? '收起' : '查看全部' }}
              </div>
            </div>
            <div v-else class="empty-photo">暂无图片</div>
          </div>

          <!-- 竞品情况截图 -->
          <div class="photo-block">
            <h4 class="photo-block-title">
              竞品情况截图
              <span
                v-if="detail.competeScreenshotPhotos && detail.competeScreenshotPhotos.length"
                class="photo-count">
                {{ detail.competeScreenshotPhotos.length }} 张
              </span>
            </h4>
            <div
              v-if="detail.competeScreenshotPhotos && detail.competeScreenshotPhotos.length"
              class="photo-grid">
              <el-image
                v-for="(img, idx) in displayPhotos('competeScreenshotPhotos')"
                :key="'screenshot-' + idx"
                :src="img"
                :preview-src-list="detail.competeScreenshotPhotos"
                :initial-index="idx"
                fit="cover"
                class="photo-img" />
              <div
                v-if="detail.competeScreenshotPhotos.length >= MAX_DISPLAY_PHOTOS"
                class="photo-more"
                @click="toggleExpandPhotos('competeScreenshotPhotos')">
                <i
                  :class="
                    isExpanded('competeScreenshotPhotos') ? 'el-icon-arrow-up' : 'el-icon-more'
                  "></i>
                {{ isExpanded('competeScreenshotPhotos') ? '收起' : '查看全部' }}
              </div>
            </div>
            <div v-else class="empty-photo">暂无图片</div>
          </div>
        </div>
      </div>

      <!-- 竞品信息 -->
      <div class="section-card">
        <div class="section-header">
          <div class="section-header-left">
            <div class="section-icon" style="background: #fff7ed; color: #f59e0b">
              <i class="el-icon-coin"></i>
            </div>
            <div>
              <h3 class="section-title">竞品信息</h3>
              <p class="section-subtitle">周边竞品充电桩情况</p>
            </div>
          </div>
        </div>
        <div class="section-content">
          <div v-if="detail.competitors && detail.competitors.length">
            <div v-for="(item, index) in detail.competitors" :key="index" class="competitor-card">
              <div class="competitor-header">
                <span class="competitor-title">竞品 {{ index + 1 }}</span>
              </div>
              <div class="info-grid">
                <div class="info-row">
                  <span class="info-label">品牌名称</span>
                  <span class="info-value">{{ item.brandName || '--' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">充电桩数量</span>
                  <span class="info-value">{{ item.pileCount ?? '--' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">电价模式</span>
                  <span class="info-value">{{ item.priceMode || '--' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">使用率</span>
                  <span class="info-value">{{ item.usageRate || '--' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">占位率</span>
                  <span class="info-value">{{ item.occupancyRate || '--' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">收费</span>
                  <span class="info-value">{{ item.chargingFee || '--' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">充电桩位置</span>
                  <span class="info-value">{{ item.pileLocation || '--' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">详细地址</span>
                  <span class="info-value">{{ item.detailAddress || '--' }}</span>
                </div>
                <div class="info-row info-row-block">
                  <span class="info-label">说明</span>
                  <div class="info-value-block">{{ item.description || '--' }}</div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="empty-photo">暂无竞品信息</div>
        </div>
      </div>

      <!-- 私桩信息 -->
      <div class="section-card">
        <div class="section-header">
          <div class="section-header-left">
            <div class="section-icon" style="background: #ecfdf5; color: #10b981">
              <i class="el-icon-finished"></i>
            </div>
            <div>
              <h3 class="section-title">私桩信息</h3>
              <p class="section-subtitle">私人充电桩情况</p>
            </div>
          </div>
        </div>
        <div class="section-content">
          <div class="info-grid info-grid-3">
            <div class="info-row">
              <span class="info-label">有无私桩</span>
              <span class="info-value">{{ detail.hasPrivatePile || '--' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">私桩数量</span>
              <span class="info-value">{{ detail.privatePileCount ?? '--' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">私桩位置</span>
              <span class="info-value">{{ detail.privatePileLocation || '--' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 项目总结 -->
      <div class="section-card">
        <div class="section-header">
          <div class="section-header-left">
            <div class="section-icon" style="background: #f3e8ff; color: #8b5cf6">
              <i class="el-icon-document"></i>
            </div>
            <div>
              <h3 class="section-title">项目总结</h3>
              <p class="section-subtitle">布桩建议与核场总结</p>
            </div>
          </div>
        </div>
        <div class="section-content">
          <div class="info-grid">
            <div class="info-row">
              <span class="info-label">布桩公式计算数量</span>
              <span class="info-value">{{ detail.formulaPileCount ?? '--' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">城市最低下限数量</span>
              <span class="info-value">{{ detail.cityMinPileCount ?? '--' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">建议安装数量</span>
              <span class="info-value highlight-text">
                {{ detail.suggestedPileCount ?? '--' }}
              </span>
            </div>
            <div class="info-row">
              <span class="info-label">建议安装位置</span>
              <span class="info-value highlight-text">
                {{ detail.suggestedInstallLocation || '--' }}
              </span>
            </div>
          </div>
          <div class="summary-block" v-if="detail.summary">
            <div class="summary-title-row">
              <h4 class="summary-title">项目核场总结</h4>
              <el-button
                type="text"
                size="mini"
                icon="el-icon-document-copy"
                class="copy-summary-btn"
                @click="handleCopySummary">
                复制
              </el-button>
            </div>
            <div class="summary-content">{{ detail.summary }}</div>
          </div>
          <div v-else class="empty-photo">暂无核场总结</div>
        </div>
      </div>
    </div>

    <el-dialog
      title="无桩电车提取数据"
      :visible.sync="evExtractedDialogVisible"
      width="720px"
      append-to-body>
      <el-table :data="pagedEvExtractedList" border stripe size="small">
        <el-table-column type="index" label="序号" width="60" align="center" />
        <el-table-column label="位置" width="80" align="center">
          <template slot-scope="{ row }">
            <span>{{ row.location || '--' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="品牌" prop="brand">
          <template slot-scope="{ row }">
            <span>{{ row.brand || '--' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="型号" prop="model">
          <template slot-scope="{ row }">
            <span>{{ row.model || '--' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="车牌号" prop="licensePlate">
          <template slot-scope="{ row }">
            <span>{{ row.licensePlate || '--' }}</span>
          </template>
        </el-table-column>
      </el-table>
      <div class="ev-extracted-pagination">
        <el-pagination
          background
          layout="total, prev, pager, next"
          :current-page.sync="evExtractedPage"
          :page-size="evExtractedPageSize"
          :total="evExtractedList.length" />
      </div>
    </el-dialog>

    <!-- 提交立项弹窗：选择BD人员 -->
    <el-dialog
      title="提交立项"
      :visible.sync="bdDialogVisible"
      width="440px"
      :close-on-click-modal="false">
      <el-form label-width="90px" @submit.native.prevent>
        <el-form-item label="BD人员" required>
          <el-select
            v-model="selectedBdId"
            filterable
            :loading="bdLoading"
            placeholder="请输入姓名或手机号搜索"
            style="width: 100%">
            <el-option
              v-for="item in bdOptions"
              :key="item.id"
              :label="`${item.accountName}（${item.phone}）`"
              :value="item.id" />
          </el-select>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="bdDialogVisible = false">取 消</el-button>
        <el-button
          size="small"
          type="primary"
          :loading="bdSubmitting"
          @click="handleConfirmSubmitInitiation">
          确 定
        </el-button>
      </span>
    </el-dialog>
  </scroll-layout>
</template>

<script>
  import {
    inspectionRecordDetail,
    inspectionRecordSubmitInitiation,
    accountManage,
  } from '@/service/apis';
  import { offlineExport } from '@/utils';

  // 每个类目默认最多展示数量
  const MAX_DISPLAY_PHOTOS = 20;

  export default {
    name: 'InspectionRecordDetail',
    data() {
      return {
        // 临时隐藏提交立项按钮，恢复时改为 false
        hideSubmitInitiation: true,
        MAX_DISPLAY_PHOTOS,
        loading: false,
        detail: {},
        evExtractedDialogVisible: false,
        evExtractedPage: 1,
        evExtractedPageSize: 10,
        expandedCategories: [],
        // 提交立项-选择BD人员弹窗
        bdDialogVisible: false,
        bdLoading: false,
        bdLoaded: false,
        bdOptions: [],
        selectedBdId: null,
        bdSubmitting: false,
      };
    },
    computed: {
      // 是否为已提交状态（仅已提交的核场记录可提交立项）
      isSubmitted() {
        return String(this.detail.status || '').toUpperCase() === 'SUBMITTED';
      },
      // 是否为已提交立项状态（立项审批流程中，不可再编辑）
      isInitiated() {
        return String(this.detail.status || '').toUpperCase() === 'INITIATION_SUBMITTED';
      },
      signalList() {
        if (!this.detail.parkingSignal) return [];
        if (Array.isArray(this.detail.parkingSignal)) return this.detail.parkingSignal;
        return String(this.detail.parkingSignal)
          .split(',')
          .map(s => s.trim())
          .filter(Boolean);
      },
      evExtractedList() {
        const photos = this.detail.evPhotos || [];
        if (!photos.length) return [];
        const brands = this.detail.evPhotoBrands || [];
        const models = this.detail.evPhotoModels || [];
        const plates = this.detail.evPhotoLicensePlates || [];
        return photos.map((photo, idx) => ({
          location: this.getEvLocation(idx) || '--',
          brand: brands[idx] || '',
          model: models[idx] || '',
          licensePlate: plates[idx] || '',
          photo,
        }));
      },
      pagedEvExtractedList() {
        const start = (this.evExtractedPage - 1) * this.evExtractedPageSize;
        return this.evExtractedList.slice(start, start + this.evExtractedPageSize);
      },
    },
    watch: {
      '$route.query.id': {
        immediate: true,
        handler(val) {
          if (val) {
            this.loadDetail();
          }
        },
      },
    },
    methods: {
      // 车位使用情况：纯数字则补充"%"
      formatParkingUsage(val) {
        if (val === null || val === undefined || val === '') return '--';
        const str = String(val).trim();
        if (/^\d+(\.\d+)?$/.test(str)) return `${str}%`;
        return val;
      },
      // 周边200米快充站：枚举为有/无，兼容历史数据（是/否及编码1/0）
      formatFastCharge(val) {
        if (val === null || val === undefined || val === '') return '--';
        const map = { 是: '有', 否: '无', 1: '有', 0: '无' };
        return map[val] || val;
      },
      // 复制项目核场总结
      handleCopySummary() {
        const text = this.detail.summary || '';
        if (!text) return;
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        try {
          document.execCommand('copy');
          this.$message.success('已复制');
        } catch (e) {
          this.$message.warning('复制失败，请手动复制');
        }
        document.body.removeChild(textarea);
      },
      // 获取限制数量的照片列表（展开时返回全部）
      displayPhotos(key) {
        const photos = this.detail[key] || [];
        if (this.expandedCategories.includes(key)) return photos;
        return photos.slice(0, MAX_DISPLAY_PHOTOS);
      },
      // 切换展开/收起照片
      toggleExpandPhotos(key) {
        const idx = this.expandedCategories.indexOf(key);
        if (idx > -1) {
          this.expandedCategories.splice(idx, 1);
        } else {
          this.expandedCategories.push(key);
        }
      },
      // 是否已展开
      isExpanded(key) {
        return this.expandedCategories.includes(key);
      },
      goBack() {
        this.$router.push({ name: 'inspectionRecordList' });
      },
      goEdit() {
        this.$router.push({ name: 'inspectionRecordEdit', query: { id: this.detail.id } });
      },
      // 提交立项：先确认提示，再弹出BD人员选择弹窗
      handleSubmitInitiation() {
        this.$confirm('提交立项后，核场记录将无法修改，是否继续？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        })
          .then(() => {
            this.selectedBdId = null;
            this.bdDialogVisible = true;
            this.requestBdAccounts();
          })
          .catch(() => {});
      },
      // 获取BD角色账号列表（姓名、手机号）
      requestBdAccounts() {
        if (this.bdLoaded) return;
        this.bdLoading = true;
        accountManage
          .getUserAccountByBd()
          .then(res => {
            this.bdOptions = res || [];
            this.bdLoaded = true;
          })
          .catch(err => {
            console.error('获取BD人员列表失败:', err);
          })
          .finally(() => {
            this.bdLoading = false;
          });
      },
      // 确认提交立项：后端调用飞书获取所选BD人员和提交人的飞书用户id
      handleConfirmSubmitInitiation() {
        if (!this.selectedBdId) {
          this.$message.warning('请选择BD人员');
          return;
        }
        this.bdSubmitting = true;
        inspectionRecordSubmitInitiation({
          recordId: this.detail.id,
          bdAccountId: this.selectedBdId,
        })
          .then(() => {
            this.bdDialogVisible = false;
            this.$message.success('提交立项成功');
            // 刷新详情数据（状态变为已提交立项，按钮同步隐藏）
            this.loadDetail();
          })
          .catch(err => {
            console.error('提交立项失败:', err);
          })
          .finally(() => {
            this.bdSubmitting = false;
          });
      },
      loadDetail() {
        this.loading = true;
        inspectionRecordDetail({ id: this.$route.query.id })
          .then(res => {
            const detail = res || {};
            this.detail = detail;
          })
          .catch(err => {
            console.error('加载详情失败:', err);
            this.$message.error('加载详情失败');
          })
          .finally(() => {
            this.loading = false;
          });
      },
      getStatusText(status) {
        const s = String(status || '').toUpperCase();
        if (s === 'DRAFT') return '草稿';
        if (s === 'SUBMITTED') return '已提交';
        if (s === 'INITIATION_SUBMITTED') return '已提交立项';
        return status || '--';
      },
      getStatusClass(status) {
        const s = String(status || '').toUpperCase();
        if (s === 'DRAFT') return 'status-draft';
        if (s === 'SUBMITTED') return 'status-submitted';
        if (s === 'INITIATION_SUBMITTED') return 'status-initiated';
        return 'status-default';
      },
      getStatusIcon(status) {
        const s = String(status || '').toUpperCase();
        if (s === 'DRAFT') return 'el-icon-document';
        if (s === 'SUBMITTED') return 'el-icon-circle-check';
        if (s === 'INITIATION_SUBMITTED') return 'el-icon-position';
        return 'el-icon-info';
      },
      getEvLocation(idx) {
        const loc = this.detail.evPhotoLocations && this.detail.evPhotoLocations[idx];
        // evPhotoLocations 有值时以它为准
        if (loc !== undefined && loc !== null) {
          if (loc === 'UNDERGROUND' || loc === '地下') return '地下';
          if (loc === 'GROUND' || loc === '地上') return '地上';
          return null; // 空字符串或其他无效值 -> 无位置
        }
        // evPhotoLocations 未设置 -> 无位置（不回退 carType，避免错误默认值）
        return null;
      },
      handleViewEvExtractedData() {
        if (!this.evExtractedList.length) {
          this.$message.warning('暂无提取数据');
          return;
        }
        this.evExtractedPage = 1;
        this.evExtractedDialogVisible = true;
      },
      handleExportEvExtractedData() {
        if (!this.evExtractedList.length) {
          this.$message.warning('暂无提取数据');
          return;
        }
        offlineExport({
          downloadType: 'project_verification_ev_export',
          jsonNode: {
            recordId: this.detail.id,
          },
        });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .inspection-record-detail {
    background: #f5f7fa;
  }

  /* ==================== 顶部 ==================== */
  .page-header {
    display: flex;
    align-items: center;
    gap: 16px;
    background: #fff;
    padding: 16px 20px;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  }

  .back-btn {
    font-size: 14px;
    color: #64748b;
    padding: 0;

    &:hover {
      color: #c9915e;
    }
  }

  .page-title {
    font-size: 18px;
    font-weight: 600;
    color: #1a1a2e;
    margin: 0;
    flex: 1;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .record-code-tag {
    font-family: 'Courier New', monospace;
    font-size: 12px;
    color: #475569;
    background: #f1f5f9;
    padding: 2px 8px;
    border-radius: 4px;
  }

  .header-actions {
    display: flex;
    gap: 8px;
  }

  /* ==================== 主体 ==================== */
  .detail-body {
    padding: 16px 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  /* AI生成内容提示 */
  .ai-notice {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 16px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
    color: #dc2626;
    font-size: 14px;
    line-height: 1.6;
  }

  .required {
    color: #ef4444;
  }

  .card {
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  }

  /* ==================== 状态栏 ==================== */
  .status-bar {
    padding: 16px 20px;
  }

  .status-info {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
  }

  .status-left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .status-label {
    font-size: 14px;
    color: #64748b;
  }

  .status-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
  }

  .status-tag i {
    font-size: 12px;
  }

  .status-draft {
    background: #fef3c7;
    color: #92400e;
  }

  .status-submitted {
    background: #d1fae5;
    color: #065f46;
  }

  .status-initiated {
    background: #dbeafe;
    color: #1e40af;
  }

  .status-default {
    background: #e0e7ff;
    color: #3730a3;
  }

  .status-meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
    font-size: 13px;
    color: #64748b;
  }

  .meta-item {
    display: inline-flex;
    align-items: center;
    gap: 4px;

    i {
      color: #94a3b8;
      font-size: 12px;
    }
  }

  .meta-time {
    color: #94a3b8;
    margin-left: 4px;
    font-size: 12px;
  }

  /* ==================== 卡片 ==================== */
  .section-card {
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    overflow: hidden;
  }

  .section-header {
    padding: 14px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid #f3f4f6;
  }

  .section-header-left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .section-icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
  }

  .section-title {
    font-size: 15px;
    font-weight: 600;
    color: #1a1a2e;
    margin: 0;
  }

  .section-subtitle {
    font-size: 12px;
    color: #94a3b8;
    margin: 2px 0 0;
  }

  .section-content {
    padding: 20px;
  }

  /* ==================== 信息行 ==================== */
  .info-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0 32px;
  }

  .info-grid-3 {
    grid-template-columns: repeat(3, 1fr);
  }

  .info-row {
    display: flex;
    align-items: flex-start;
    padding: 8px 0;
    border-bottom: 1px solid #f9fafb;
    font-size: 14px;
  }

  .info-label {
    width: 140px;
    color: #6b7280;
    flex-shrink: 0;
  }

  .info-value {
    flex: 1;
    color: #1f2937;
    word-break: break-all;
  }

  .info-value.highlight-text {
    color: #c9915e;
    font-weight: 500;
  }

  .info-row-block {
    flex-direction: column;
    gap: 8px;
  }

  .info-row-block .info-label {
    width: auto;
  }

  .info-value-block {
    background: #fafafa;
    border-radius: 6px;
    padding: 10px 12px;
    color: #1f2937;
    line-height: 1.6;
    font-size: 14px;
    white-space: pre-wrap;
  }

  /* ==================== 子分组 ==================== */
  .sub-group {
    background: #fafafa;
    border-radius: 10px;
    padding: 12px;
    margin-top: 12px;
  }

  .sub-group-title {
    font-size: 12px;
    font-weight: 600;
    color: #c9915e;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 6px;

    i {
      font-size: 11px;
    }
  }

  /* ==================== 信号标签 ==================== */
  .signal-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }

  .signal-tag {
    display: inline-block;
    padding: 2px 8px;
    background: #fdf4e8;
    color: #c9915e;
    border-radius: 4px;
    font-size: 12px;
  }

  /* ==================== 图片 ==================== */
  .photo-block {
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .photo-block-title {
    font-size: 13px;
    font-weight: 500;
    color: #1f2937;
    margin: 0 0 8px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .photo-count {
    color: #94a3b8;
    font-size: 12px;
    font-weight: 400;
  }

  .photo-block-actions {
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  .ev-extracted-pagination {
    margin-top: 12px;
    text-align: right;
  }

  .photo-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 8px;
  }

  .photo-item {
    position: relative;
    aspect-ratio: 1;
    border-radius: 6px;
    overflow: hidden;
    background: #f5f2ed;
  }

  .photo-img {
    width: 100%;
    aspect-ratio: 1;
    object-fit: cover;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 1px solid #f3f4f6;

    &:hover {
      opacity: 0.85;
      transform: scale(1.02);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
  }

  .photo-location-tags {
    position: absolute;
    bottom: 4px;
    left: 4px;
    display: flex;
    gap: 4px;
    z-index: 5;
  }

  .loc-tag {
    padding: 2px 6px;
    font-size: 10px;
    font-weight: 600;
    border-radius: 4px;
    user-select: none;
    line-height: 1.4;

    &.active {
      color: #fff;
    }

    &.ground-tag.active {
      background: linear-gradient(135deg, #c9915e 0%, #a67642 100%);
      box-shadow: 0 2px 6px rgba(201, 145, 94, 0.3);
    }

    &.underground-tag.active {
      background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
      box-shadow: 0 2px 6px rgba(59, 130, 246, 0.3);
    }

    &.inactive {
      background: rgba(128, 128, 128, 0.8);
      color: rgba(255, 255, 255, 0.5);
    }
  }

  .ev-info-tags {
    position: absolute;
    top: 4px;
    left: 4px;
    display: flex;
    flex-wrap: wrap;
    gap: 3px;
    z-index: 5;
    max-width: calc(100% - 8px);
  }

  .ev-info-tag {
    padding: 1px 5px;
    font-size: 9px;
    font-weight: 500;
    border-radius: 3px;
    background: rgba(0, 0, 0, 0.5);
    color: #fff;
    line-height: 1.4;
    white-space: nowrap;
    backdrop-filter: blur(4px);
  }

  .empty-photo {
    color: #94a3b8;
    font-size: 12px;
    text-align: center;
    padding: 12px 0;
  }

  /* ==================== 竞品 ==================== */
  .competitor-card {
    background: #fafafa;
    border-radius: 10px;
    padding: 14px;
    border: 1px solid #e8e4df;
    margin-bottom: 10px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .competitor-header {
    margin-bottom: 8px;
    padding-bottom: 8px;
    border-bottom: 1px solid #f3f4f6;
  }

  .competitor-title {
    font-size: 14px;
    font-weight: 600;
    color: #1a1a2e;
  }

  /* ==================== 项目总结 ==================== */
  .summary-block {
    margin-top: 12px;
  }

  .summary-title {
    font-size: 13px;
    font-weight: 500;
    color: #1f2937;
    margin: 0 0 8px;
  }

  .summary-title-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 8px;
  }

  .summary-title-row .summary-title {
    margin: 0;
  }

  .copy-summary-btn {
    padding: 2px 6px;
    font-size: 12px;
    color: #64748b;

    &:hover {
      color: #c9915e;
    }
  }

  .summary-content {
    background: #fafafa;
    border-radius: 8px;
    padding: 12px 16px;
    color: #1f2937;
    line-height: 1.8;
    font-size: 14px;
    white-space: pre-wrap;
  }

  @media (max-width: 900px) {
    .info-grid,
    .info-grid-3 {
      grid-template-columns: 1fr;
    }
    .info-label {
      width: 110px;
    }
  }

  .photo-more {
    min-height: 96px;
    border-radius: 8px;
    background: #f5f2ed;
    color: #94a3b8;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    cursor: pointer;
    transition: background 0.2s;

    i {
      font-size: 20px;
      margin-bottom: 4px;
    }

    &:hover {
      background: #ebe7e0;
      color: #64748b;
    }
  }
</style>
